import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BarChart3,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Database,
  Handshake,
  Home,
  KeyRound,
  MapPinned,
  MessageCircle,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Video,
} from "lucide-react";
import CalendlyButton from "../components/CalendlyButton";

const brandBlue = "#003366";
const brandOrange = "#da3e13";
const brandGold = "#fbb40b";

const defaultJuliaContext =
  "Malay spoke with Julia about buying an investment property with her husband in Townsville. Their plan is to hold the property for 8-10 years, then consolidate the sale of the new purchase and their family home to buy their forever home on the Sunshine Coast in around 10 years. Julia is looking for a house on at least 500 sqm, ideally 3-4 bedrooms, 1-2 bathrooms and 2 parking spaces, with a budget around $700,000. She plans to fly to Townsville with her husband next week to inspect areas herself, is already certain she wants to engage a buyer's agent, has spoken with other BAs, and asked for a brochure/proposal to review.";

const defaultGoals =
  "Buy a Townsville investment-grade house that balances rental demand, land component, long-term growth potential and resale flexibility. The asset should support the broader 10-year plan of eventually moving into a forever home on the Sunshine Coast.";

const defaultFocus =
  "Use NPC's buyer-side strategy, data-led due diligence, local agent relationships and Propsourcing access to identify suitable on-market, pre-market and off-market opportunities, compare areas, avoid poor-fit streets/assets, and negotiate with confidence.";

const proofResults = [
  { label: "+11% capital growth", detail: "in 6 months", image: "/ravi.webp" },
  { label: "+6.5% rental yield", detail: "high-return suburb", image: "/deepak.jpg" },
  { label: "+$150K equity", detail: "in 12 months", image: "/gopi.webp" },
  { label: "Positive cashflow", detail: "from day one", image: "/Tarragona.jpg" },
  { label: "Undermarket deal", detail: "secured below valuation", image: "/undermarket.jpg" },
  { label: "Off-market", detail: "secured before public listing", image: "/offmarket_mohan.webp" },
];

const selectedCaseStudies = [
  { state: "QLD", land: "599 sqm", price: "$441,000", rent: "$595/wk", yield: "7.0%" },
  { state: "QLD", land: "846 sqm", price: "$564,000", rent: "$750/wk", yield: "6.9%" },
  { state: "QLD", land: "700 sqm", price: "$550,000", rent: "$650/wk", yield: "6.1%" },
  { state: "QLD", land: "558 sqm", price: "$540,000", rent: "$620/wk", yield: "6.0%" },
];

const reviews = [
  {
    name: "Himanshu Chahar",
    quote: "Amit is an absolute gem of a buyer's agent.",
    detail:
      "He made the entire process smooth, stress-free and enjoyable. His deep market knowledge and genuine care for clients set him apart.",
  },
  {
    name: "Deepak Verma",
    quote: "An amazing experience from start to finish.",
    detail:
      "Amit guided me with professionalism and care. His honesty, attention to detail and negotiation skills are unmatched.",
  },
  {
    name: "Senthilnathan Mohanasundaram",
    quote: "Helped us buy three homes across Australia.",
    detail:
      "Super professional, got us fantastic deals. Couldn't have asked for a better team to work with.",
  },
];

const journey = [
  "2022: Began our journey in real estate advisory and launched National Property Consultant.",
  "2023: Established a trusted agent network and supported investors across Australia.",
  "2024: Expanded nationwide with stronger systems and strategic partnerships.",
  "2025: Continued helping clients acquire high-performing investment properties.",
  "2025: Developed and launched Propsourcing.com.au, our proprietary sourcing platform.",
];

const dataApproach = [
  "Suburb fundamentals: population drivers, employment nodes, rental demand, vacancy risk and infrastructure.",
  "Asset quality: land size, property type, layout, maintenance risk, tenant appeal and resale depth.",
  "Comparable sales and rental evidence before making an offer.",
  "Street and micro-location review, including flood, zoning, overlays and local drawbacks where relevant.",
  "Cashflow, yield and long-term portfolio fit against the client's 8-10 year plan.",
];

const inclusions = [
  {
    title: "Strategy-first buyer brief",
    text: "Clarify goals, budget, finance readiness, preferred property profile, risk tolerance and the long-term Sunshine Coast plan.",
    icon: ClipboardCheck,
  },
  {
    title: "Townsville area and asset research",
    text: "Shortlist suitable suburbs, streets and property types using data, local feedback and investment fundamentals.",
    icon: MapPinned,
  },
  {
    title: "Property search and shortlisting",
    text: "Source on-market, pre-market and off-market opportunities through agent relationships and Propsourcing.",
    icon: Search,
  },
  {
    title: "Due diligence and risk review",
    text: "Assess comparable sales, yield, rental demand, overlays, property condition, building reports and long-term suitability.",
    icon: ShieldCheck,
  },
  {
    title: "Negotiation and acquisition support",
    text: "Negotiate with selling agents, guide offer terms, manage auction or pre-auction strategy where relevant.",
    icon: Handshake,
  },
  {
    title: "Settlement and post-purchase direction",
    text: "Coordinate with broker, conveyancer, property manager and inspection providers through settlement and next steps.",
    icon: KeyRound,
  },
];

const processSteps = [
  "Video call to answer questions and discuss the commercial part",
  "Confirm buying brief and engagement",
  "Townsville suburb and asset strategy",
  "Search, agent calls and Propsourcing review",
  "Shortlist, inspect and compare opportunities",
  "Due diligence and negotiation",
  "Contract, reports and settlement",
  "Post-purchase and long-term portfolio check-in",
];

const getInitialProposalContext = () => {
  if (typeof window === "undefined") {
    return {
      client: "Julia",
      insights: defaultJuliaContext,
      goals: defaultGoals,
      focus: defaultFocus,
    };
  }

  const params = new URLSearchParams(window.location.search);

  return {
    client: params.get("client") || "Julia",
    insights: params.get("insights") || defaultJuliaContext,
    goals: params.get("goals") || defaultGoals,
    focus: params.get("focus") || defaultFocus,
  };
};

const BuyersAgencyProposalPage = () => {
  const initialContext = useMemo(getInitialProposalContext, []);
  const [clientName, setClientName] = useState(initialContext.client);
  const [conversationInsights, setConversationInsights] = useState(
    initialContext.insights
  );
  const [clientGoals, setClientGoals] = useState(initialContext.goals);
  const [recommendedFocus, setRecommendedFocus] = useState(initialContext.focus);

  const displayClientName = clientName.trim() || "Private Client";

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const previousTitle = document.title;
    const robotsMeta = document.querySelector('meta[name="robots"]');
    const previousRobotsContent = robotsMeta?.getAttribute("content") || "";
    const meta = robotsMeta || document.createElement("meta");

    if (!robotsMeta) {
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }

    document.title = `${displayClientName} Buyers Agency Proposal | National Property Consultant`;
    meta.setAttribute("content", "noindex,nofollow");

    return () => {
      document.title = previousTitle;
      if (robotsMeta) {
        meta.setAttribute("content", previousRobotsContent);
      } else {
        meta.remove();
      }
    };
  }, [displayClientName]);

  return (
    <main className="bg-[#f7f8fb] text-slate-900">
      <section className="relative overflow-hidden bg-[#003366]">
        <div className="absolute inset-0 opacity-20">
          <img src="/whowehelp.webp" alt="" className="h-full w-full object-cover" />
        </div>
        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-14 md:grid-cols-[1.1fr_0.9fr] md:px-8 md:py-20">
          <div className="text-white">
            <p className="mb-4 inline-flex rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-white/80">
              Private Buyers Agency Proposal
            </p>
            <h1 className="max-w-4xl text-4xl font-extrabold leading-tight md:text-6xl">
              A Townsville investment plan for {displayClientName}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-white/85">
              Prepared by National Property Consultant as a client review
              document covering the brief, our process, proof points, data-led
              approach, Propsourcing advantage and next steps.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <CalendlyButton text="Book Video Call" />
              <a
                href="mailto:info@nationalpropertyconsultant.com.au?subject=Buyers Agency Proposal Questions"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/35 px-6 py-3 font-semibold text-white transition hover:bg-white hover:text-[#003366]"
              >
                Send questions
                <MessageCircle className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-white/15 bg-white/95 p-6 shadow-2xl md:p-8">
            <div className="flex items-center gap-4 border-b border-slate-200 pb-5">
              <img
                src="/NPC.png"
                alt="National Property Consultant"
                className="h-14 w-auto"
              />
              <div>
                <div className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Prepared by
                </div>
                <div className="text-xl font-bold" style={{ color: brandBlue }}>
                  National Property Consultant
                </div>
              </div>
            </div>
            <div className="mt-6 space-y-4">
              {[
                ["Client", displayClientName],
                ["Target location", "Townsville, QLD"],
                ["Budget", "Around $700,000"],
                ["Property profile", "House, 500+ sqm, 3-4 bed, 1-2 bath, 2 parking"],
                ["Next action", "Video call to answer questions and discuss commercials"],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl bg-slate-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {label}
                  </div>
                  <div className="mt-1 font-semibold text-slate-900">{value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-12 md:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-8">
          <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
            <div>
              <p
                className="text-sm font-bold uppercase tracking-[0.18em]"
                style={{ color: brandOrange }}
              >
                Client Context
              </p>
              <h2
                className="mt-3 text-3xl font-extrabold"
                style={{ color: brandBlue }}
              >
                Julia's brief and decision context
              </h2>
              <p className="mt-4 leading-7 text-slate-600">
                This proposal is designed to help Julia and her husband compare
                the value of a buyer's agent, ask any final questions and then
                decide whether to engage NPC before their Townsville trip.
              </p>
            </div>

            <div className="grid gap-4">
              <label className="grid gap-2">
                <span className="text-sm font-semibold text-slate-700">
                  Client name
                </span>
                <input
                  value={clientName}
                  onChange={(event) => setClientName(event.target.value)}
                  placeholder="Enter client name"
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base outline-none transition focus:border-[#003366] focus:ring-2 focus:ring-[#003366]/15"
                />
              </label>

              <label className="grid gap-2">
                <span className="text-sm font-semibold text-slate-700">
                  Conversation insights
                </span>
                <textarea
                  value={conversationInsights}
                  onChange={(event) =>
                    setConversationInsights(event.target.value)
                  }
                  rows={7}
                  className="w-full resize-y rounded-xl border border-slate-300 px-4 py-3 text-base leading-7 outline-none transition focus:border-[#003366] focus:ring-2 focus:ring-[#003366]/15"
                />
              </label>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-2">
                  <span className="text-sm font-semibold text-slate-700">
                    Client goals and constraints
                  </span>
                  <textarea
                    value={clientGoals}
                    onChange={(event) => setClientGoals(event.target.value)}
                    rows={5}
                    className="w-full resize-y rounded-xl border border-slate-300 px-4 py-3 text-base leading-7 outline-none transition focus:border-[#003366] focus:ring-2 focus:ring-[#003366]/15"
                  />
                </label>

                <label className="grid gap-2">
                  <span className="text-sm font-semibold text-slate-700">
                    Recommended buying focus
                  </span>
                  <textarea
                    value={recommendedFocus}
                    onChange={(event) =>
                      setRecommendedFocus(event.target.value)
                    }
                    rows={5}
                    className="w-full resize-y rounded-xl border border-slate-300 px-4 py-3 text-base leading-7 outline-none transition focus:border-[#003366] focus:ring-2 focus:ring-[#003366]/15"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-5 py-14 md:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p
              className="text-sm font-bold uppercase tracking-[0.18em]"
              style={{ color: brandOrange }}
            >
              Recommended Strategy
            </p>
            <h2
              className="mt-3 text-3xl font-extrabold md:text-4xl"
              style={{ color: brandBlue }}
            >
              The brief is not just to buy in Townsville. It is to buy the right asset for the 10-year plan.
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              Julia's objective has two stages: acquire an investment property
              now, hold it for 8-10 years, then use the combined position of the
              investment property and family home to help purchase the forever
              home on the Sunshine Coast. That means the purchase needs to be
              reviewed for growth, income, risk and future resale depth.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              ["Asset", "House on at least 500 sqm with broad tenant and resale appeal.", Home],
              ["Numbers", "Purchase around $700,000 with rent and cashflow tested before offer.", BarChart3],
              ["Risk", "Avoid streets, overlays, maintenance issues and low-demand pockets.", ShieldCheck],
            ].map(([title, text, Icon]) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-[#f9f9f9] p-6"
              >
                <Icon className="h-7 w-7" style={{ color: brandOrange }} />
                <h3 className="mt-4 text-xl font-bold" style={{ color: brandBlue }}>
                  {title}
                </h3>
                <p className="mt-2 leading-7 text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-14 md:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.95fr_1.05fr]">
          <div>
            <p
              className="text-sm font-bold uppercase tracking-[0.18em]"
              style={{ color: brandOrange }}
            >
              Why NPC
            </p>
            <h2
              className="mt-3 text-3xl font-extrabold md:text-4xl"
              style={{ color: brandBlue }}
            >
              Buyer-side advice backed by strategy, data and sourcing systems
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              National Property Consultant helps busy professionals, first-time
              investors, SMSF buyers and owner-occupiers make strategic property
              decisions with off-market access, due diligence and transparent
              communication from search to settlement.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {[
              ["Client-first", "Advice is shaped around the buyer's goals, not a property that happens to be available.", Target],
              ["Data-backed", "Suburb, street, rental, comparable sales and risk checks before a recommendation.", Database],
              ["Negotiation", "Buyer-side negotiation support so Julia does not have to rely on selling-agent guidance.", Handshake],
              ["Long-term", "The plan considers the 8-10 year hold and future Sunshine Coast objective.", TrendingUp],
            ].map(([title, text, Icon]) => (
              <div key={title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <Icon className="h-6 w-6" style={{ color: brandOrange }} />
                <h3 className="mt-3 font-bold" style={{ color: brandBlue }}>
                  {title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#003366] px-5 py-14 text-white md:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
          <div>
            <Sparkles className="h-8 w-8" style={{ color: brandGold }} />
            <h2 className="mt-4 text-3xl font-extrabold md:text-4xl">
              Propsourcing is our sourcing advantage
            </h2>
            <p className="mt-4 text-lg leading-8 text-white/80">
              Many buyer's agents talk about off-market access. NPC has built
              Propsourcing, a proprietary platform that helps discover, shortlist
              and manage investment-grade opportunities with clearer visibility
              across on-market, pre-market and off-market channels.
            </p>
            <div className="mt-6 grid gap-3">
              {[
                "Over 700 off-market properties received in the last 6 months through the wider Propsourcing flow.",
                "Verified agent network and weekly property drops.",
                "A practical way to compare opportunities instead of relying only on sales-agent emails and public portals.",
              ].map((item) => (
                <div key={item} className="flex gap-3 rounded-xl bg-white/10 p-4 ring-1 ring-white/15">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: brandGold }} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-white/15 bg-white/10 p-3">
            <img
              src="/propsourcing-platform-screenshot.png"
              alt="Propsourcing platform"
              className="w-full rounded-xl"
            />
          </div>
        </div>
      </section>

      <section className="bg-white px-5 py-14 md:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p
                className="text-sm font-bold uppercase tracking-[0.18em]"
                style={{ color: brandOrange }}
              >
                Data-Led Due Diligence
              </p>
              <h2
                className="mt-3 text-3xl font-extrabold md:text-4xl"
                style={{ color: brandBlue }}
              >
                How we evaluate the purchase before you commit
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                The goal is to reduce guesswork. For Julia's Townsville brief,
                we would combine macro research, suburb filtering, micro-location
                review, comparable evidence and property-level due diligence.
              </p>
            </div>
            <div className="grid gap-3">
              {dataApproach.map((item) => (
                <div key={item} className="flex gap-3 rounded-xl border border-slate-200 bg-[#f9f9f9] p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: brandOrange }} />
                  <span className="leading-7 text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-5 py-14 md:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p
              className="text-sm font-bold uppercase tracking-[0.18em]"
              style={{ color: brandOrange }}
            >
              Success Stories
            </p>
            <h2
              className="mt-3 text-3xl font-extrabold md:text-4xl"
              style={{ color: brandBlue }}
            >
              Real investment outcomes from the NPC website
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              These examples show the type of outcome-focused thinking NPC uses:
              yield, equity, growth, cashflow, purchase price discipline and
              access to opportunities before the broader market sees them.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {proofResults.map((result) => (
              <div key={result.label} className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 shadow-sm">
                <img
                  src={result.image}
                  alt=""
                  className="h-44 w-full object-cover opacity-65"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-5 text-white">
                  <div className="text-xl font-extrabold">{result.label}</div>
                  <div className="mt-1 text-sm text-white/85">{result.detail}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="grid grid-cols-5 bg-slate-50 px-4 py-3 text-sm font-bold uppercase tracking-wide text-slate-500">
              <span>State</span>
              <span>Land</span>
              <span>Purchase</span>
              <span>Rent</span>
              <span>Yield</span>
            </div>
            {selectedCaseStudies.map((item) => (
              <div key={`${item.price}-${item.land}`} className="grid grid-cols-5 border-t border-slate-100 px-4 py-4 text-sm text-slate-700">
                <span className="font-semibold text-slate-900">{item.state}</span>
                <span>{item.land}</span>
                <span>{item.price}</span>
                <span>{item.rent}</span>
                <span className="font-bold" style={{ color: brandOrange }}>{item.yield}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-5 py-14 md:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p
              className="text-sm font-bold uppercase tracking-[0.18em]"
              style={{ color: brandOrange }}
            >
              Client Reviews
            </p>
            <h2
              className="mt-3 text-3xl font-extrabold md:text-4xl"
              style={{ color: brandBlue }}
            >
              What clients say about working with Amit and NPC
            </h2>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {reviews.map((review) => (
              <div key={review.name} className="rounded-2xl border border-slate-200 bg-[#f9f9f9] p-6">
                <div className="flex gap-1" style={{ color: brandGold }}>
                  {[0, 1, 2, 3, 4].map((star) => (
                    <Star key={star} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <p className="mt-4 font-bold" style={{ color: brandBlue }}>
                  "{review.quote}"
                </p>
                <p className="mt-3 leading-7 text-slate-600">{review.detail}</p>
                <p className="mt-4 text-sm font-semibold text-slate-500">
                  {review.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-14 md:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p
              className="text-sm font-bold uppercase tracking-[0.18em]"
              style={{ color: brandOrange }}
            >
              Our Journey
            </p>
            <h2
              className="mt-3 text-3xl font-extrabold md:text-4xl"
              style={{ color: brandBlue }}
            >
              Built from advisory, systems and real buyer outcomes
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              NPC has grown from real estate advisory into a nationwide buyer's
              agency and sourcing platform, with a focus on ethical advice,
              transparency and long-term relationships.
            </p>
          </div>
          <div className="grid gap-3">
            {journey.map((item) => (
              <div key={item} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <Building2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: brandOrange }} />
                <span className="leading-7 text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-5 py-14 md:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p
              className="text-sm font-bold uppercase tracking-[0.18em]"
              style={{ color: brandOrange }}
            >
              Proposed Scope
            </p>
            <h2
              className="mt-3 text-3xl font-extrabold md:text-4xl"
              style={{ color: brandBlue }}
            >
              What NPC would do for Julia and her husband
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              The service is designed to remove guesswork from buying. NPC
              manages the strategy, search, due diligence and acquisition process
              while keeping Julia informed and in control.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {inclusions.map(({ title, text, icon: Icon }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-[#f9f9f9] p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#003366] text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-bold" style={{ color: brandBlue }}>
                  {title}
                </h3>
                <p className="mt-2 leading-7 text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-14 md:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p
              className="text-sm font-bold uppercase tracking-[0.18em]"
              style={{ color: brandOrange }}
            >
              Process
            </p>
            <h2
              className="mt-3 text-3xl font-extrabold md:text-4xl"
              style={{ color: brandBlue }}
            >
              From proposal review to settlement
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              The immediate next action is a video call to walk through Julia's
              questions and discuss the commercial part of the engagement. After
              that, we can formalise the brief and move quickly if she wants
              support before travelling to Townsville.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {processSteps.map((step, index) => (
              <div key={step} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ backgroundColor: brandBlue }}>
                  {index + 1}
                </div>
                <div className="font-semibold text-slate-800">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 pb-16 md:px-8">
        <div className="mx-auto max-w-7xl rounded-3xl bg-[#da3e13] p-8 text-white md:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <div className="flex items-center gap-3">
                <Video className="h-7 w-7" />
                <h2 className="text-3xl font-extrabold">
                  Next step: video call and commercial discussion
                </h2>
              </div>
              <p className="mt-3 max-w-3xl text-white/85">
                We will answer any questions from this proposal, confirm the
                buying brief, explain how NPC would run the Townsville search,
                and discuss the commercial terms for buyer's agency engagement.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <CalendlyButton text="Book Video Call" />
              <a
                href="mailto:info@nationalpropertyconsultant.com.au?subject=Julia Buyers Agency Proposal"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 font-bold text-[#da3e13] transition hover:bg-slate-100"
              >
                <ArrowRight className="h-4 w-4" />
                Email NPC
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BuyersAgencyProposalPage;
